/* ===========================================================================
   Shipping estimate box — behaviour
   ---------------------------------------------------------------------------
   One custom element, used twice on the cart page (page + drawer). Plain JS,
   its own closure per element: a behaviour that appears more than once on a
   page is never handed to Alpine in this theme (PROJECT.md §9 — one Alpine.data
   object was shared by two elements and the second init overwrote the first).

   The element is destroyed and rebuilt every time the cart re-renders: both
   cart.js and cart-drawer.js replace #MainCart__Items and #CartDrawer with
   innerHTML. That is what makes a custom element the right shape here —
   connectedCallback runs again on the new copy, reads the remembered ZIP and
   asks again, so a quantity change re-prices the shipping without the shopper
   typing anything.

   ⚠️ This file is loaded by sections/main-cart.liquid and by
   snippets/cart-drawer.liquid — NOT by the snippet that draws the box. It has
   to sit in markup that is in the first document: when the cart starts empty
   the drawer's box arrives through an `innerHTML` rebuild, and a <script src>
   inserted that way never runs. Registered late is the same as not registered
   at all, and the box then renders with no ZIP field.

   The endpoint is the authority on every number. Nothing here adds, discounts
   or rounds a price; the only thing done to an amount is putting it in the
   shop's money format.
   =========================================================================== */

if (!customElements.get('kbw-shipping-estimate')) {
  /* The ZIP is remembered per visitor. localStorage throws outright in a
     locked-down browser, so every touch is wrapped — a shopper with storage
     disabled simply types the ZIP again. */
  const ZIP_KEY = 'kbw:shipping-estimate:zip';

  const readZip = () => {
    try {
      return window.localStorage.getItem(ZIP_KEY) || '';
    } catch (e) {
      return '';
    }
  };

  const writeZip = (zip) => {
    try {
      window.localStorage.setItem(ZIP_KEY, zip);
    } catch (e) {
      /* nothing to do: the ZIP is a convenience, not state we depend on */
    }
  };

  /* Slow is the same as broken here — the cart must never wait on us. */
  const REQUEST_TIMEOUT_MS = 12000;
  const DEBOUNCE_MS = 400;

  /* Whether the feature is switched on lives in the app, not the theme. One
     GET answers it for the whole page, shared by the cart page's box and the
     drawer's, and it is asked once per page load. */
  let enabledPromise = null;

  const isFeatureEnabled = (endpoint) => {
    if (!enabledPromise) {
      enabledPromise = fetch(endpoint, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => (j && j.ok ? j.enabled !== false : true))
        /* A failed GET is not an answer of "off". The box stays, and the POST
           decides what the shopper sees. */
        .catch(() => true);
    }
    return enabledPromise;
  };

  /* --- money ---------------------------------------------------------------
     The same arithmetic as theme.js's formatMoney, written out here so the box
     never depends on another file's load order. Cents in, the shop's own
     format string out. */
  const delimit = (cents, precision, thousands, decimal) => {
    const value = (cents / 100).toFixed(precision);
    const parts = value.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  };

  const money = (cents, format) => {
    const fallback = delimit(cents, 2, ',', '.');
    if (!format) return fallback;
    const match = /\{\{\s*(\w+)\s*\}\}/.exec(format);
    if (!match) return fallback;
    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = delimit(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = delimit(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = delimit(cents, 0, '.', ',');
        break;
      default:
        value = delimit(cents, 2, ',', '.');
    }
    return format.replace(match[0], value);
  };

  /* --- text ----------------------------------------------------------------
     The owner's strings carry [tokens]; the data fills them. A token with no
     value is emptied rather than left on screen as "[zip]". */
  const fill = (text, values) =>
    String(text || '').replace(/\[(\w+)\]/g, (whole, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : ''
    );

  const escapeHtml = (text) =>
    String(text == null ? '' : text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  /* The service names three reasons because they want three sentences, and it
     is the service — not this page — that knows which is which:

       unsupported_country     a country was named and it was not the US
       unsupported_destination no country named, and what was typed has letters
       unknown_destination     US-shaped but unplaceable: PO box, military,
                               or simply mistyped

     The first two mean the same thing to a shopper (this is not a US address)
     and share the wording; the third is a typo and says so. */
  const NON_US_REASONS = ['unsupported_country', 'unsupported_destination'];

  /* "Try again" belongs at the end of the owner's own sentence, not on a line
     of its own: a fourth line makes the service-unavailable panel taller than
     every other state, which is the one thing this box must never do. The
     owner's richtext always arrives wrapped in <p>. */
  const appendInline = (html, extra) => {
    const text = String(html || '');
    return /<\/p>\s*$/i.test(text)
      ? text.replace(/<\/p>(\s*)$/i, ' ' + extra + '</p>$1')
      : text + ' ' + extra;
  };

  const INFO_ICON =
    '<svg class="kbw-ship__msgicon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" stroke-width="2"/>' +
    '<path d="M12 7.6v5.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
    '<circle cx="12" cy="16.6" r="1.2" fill="currentColor"/></svg>';

  const TICK_ICON =
    '<svg class="kbw-ship__freeicon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" stroke-width="2"/>' +
    '<path d="M7.9 12.2l2.7 2.7 5.4-5.8" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const chevron = (up) =>
    '<svg viewBox="0 0 10 10" aria-hidden="true" focusable="false"><path d="' +
    (up ? 'M1 6.5 5 2.5l4 4' : 'M1 3.5 5 7.5l4-4') +
    '" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  class KbwShippingEstimate extends HTMLElement {
    connectedCallback() {
      if (this._inited) return;
      this._inited = true;

      this.config = this._readConfig();
      if (!this.config) return;

      this.form = this.querySelector('[data-kbw-ship-form]');
      this.input = this.querySelector('[data-kbw-ship-zip]');
      this.button = this.querySelector('[data-kbw-ship-submit]');
      this.buttonLabel = this.querySelector('[data-kbw-ship-btn-label]');
      this.panel = this.querySelector('[data-kbw-ship-panel]');
      this.intro = this.querySelector('[data-kbw-ship-intro]');
      this.progress = this.querySelector('[data-kbw-ship-progress]');
      this.mount = this.closest('[data-kbw-ship-mount]') || this;

      if (!this.form || !this.input || !this.button || !this.panel) return;

      this.items = Array.isArray(this.config.items) ? this.config.items : [];
      this.itemsStale = false;
      this.breakdownOpen = false;
      this.lastResult = null;
      this.timer = null;
      this.controller = null;
      this.timeoutId = null;

      /* The field row is a <div>, not a <form>: on the cart page the card sits
         inside the theme's own `{% form 'cart' %}` and the parser throws away
         a nested form element, taking the field with it. So the two things a
         form was giving us are wired by hand — the button's click, and Enter
         in the field. Enter must not bubble to the cart form either; that
         would post the cart and leave the page. */
      this._boundSubmit = (event) => {
        if (event) event.preventDefault();
        this._estimate(this.input.value);
      };
      this._boundKeydown = (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        event.stopPropagation();
        this._estimate(this.input.value);
      };
      this._boundPanelClick = (event) => this._onPanelClick(event);
      this._boundCartUpdate = () => {
        this.itemsStale = true;
        if (this._zipInPlay()) this._schedule(DEBOUNCE_MS);
      };

      this.button.addEventListener('click', this._boundSubmit);
      this.input.addEventListener('keydown', this._boundKeydown);
      this.panel.addEventListener('click', this._boundPanelClick);
      document.addEventListener('maximize:cart:updated', this._boundCartUpdate);

      /* The form only appears once it can do something. */
      this.classList.add('kbw-ship--ready');

      const saved = readZip();
      if (saved) {
        this.input.value = saved;
        this._setButtonLabel(this.config.text.buttonRepeat);
      }

      isFeatureEnabled(this.config.endpoint).then((enabled) => {
        if (!enabled) {
          this._featureOff();
          return;
        }
        if (saved && this.isConnected) this._schedule(60);
      });
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this._boundSubmit);
      if (this.input) this.input.removeEventListener('keydown', this._boundKeydown);
      if (this.panel) this.panel.removeEventListener('click', this._boundPanelClick);
      document.removeEventListener('maximize:cart:updated', this._boundCartUpdate);
      this._cancelRequest();
      if (this.timer) clearTimeout(this.timer);
      /* An element that is moved rather than replaced must come back alive,
         not come back with its listeners gone. */
      this._inited = false;
    }

    /* --- plumbing ---------------------------------------------------------- */

    _readConfig() {
      const node = this.querySelector('[data-kbw-ship-config]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        console.error('kbw-shipping-estimate: unreadable config', e);
        return null;
      }
    }

    _zipInPlay() {
      return Boolean((this.input.value || '').trim());
    }

    /* Our box goes, and nothing takes its place. Used for the two cases where
       there is nothing useful to show and nothing useful to fall back to: an
       empty cart, and an owner who chose silence over an apology when a
       request fails. The feature is still ON in these cases, so the theme's
       own estimator stays where it is — hidden. */
    _hide() {
      this.mount.hidden = true;
    }

    /* The feature itself is switched off in the app. That is not our failure
       to report, it is a different product: our box steps aside and the
       theme's own estimator — rendered alongside us all along — takes the
       slot, on the cart page and in the drawer alike. One flag on the root
       element, set once, read by CSS, and it survives every cart re-render
       because the root element is the one thing innerHTML never replaces. */
    _featureOff() {
      this.mount.hidden = true;
      document.documentElement.setAttribute('data-kbw-ship-feature', 'off');
    }

    _setButtonLabel(text) {
      if (this.buttonLabel) this.buttonLabel.textContent = text;
    }

    _schedule(wait) {
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => this._estimate(this.input.value), wait);
    }

    _cancelRequest() {
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    }

    /* The lines as the cart holds them now. The snippet rendered the cart it
       was built from, which is current until something changes it; after that
       the cart itself is asked. */
    async _currentItems() {
      if (!this.itemsStale) return this.items;
      try {
        const response = await fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return this.items;
        const cart = await response.json();
        this.items = (cart.items || []).map((line) => ({
          key: line.key,
          productId: line.product_id,
          variantId: line.variant_id,
          quantity: line.quantity,
        }));
        this.itemsStale = false;
      } catch (e) {
        /* Keep the lines we have; a stale estimate is better than none, and
           the cart re-render will bring fresh ones along anyway. */
      }
      return this.items;
    }

    /* --- the request ------------------------------------------------------- */

    async _estimate(rawZip) {
      const zip = String(rawZip || '').trim();
      if (!zip) {
        this.input.focus();
        return;
      }

      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      /* A new question cancels the old one: the answer to a ZIP the shopper
         has already replaced must never land in the panel. */
      this._cancelRequest();

      writeZip(zip);
      this.breakdownOpen = false;
      this._renderLoading();

      const items = await this._currentItems();
      if (!items.length) {
        this._hide();
        return;
      }

      const controller = new AbortController();
      this.controller = controller;
      this.timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      let payload = null;
      try {
        const response = await fetch(this.config.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          /* The country travels only when the storefront genuinely knows it —
             see the snippet. Empty means "I don't know", and the service then
             treats the postcode as the only test. */
          body: JSON.stringify({ zip: zip, countryCode: this.config.countryCode || '', items: items }),
          signal: controller.signal,
        });
        payload = await response.json();
        if (!response.ok) payload = payload && payload.ok === false ? payload : { ok: false };
      } catch (e) {
        payload = null;
      } finally {
        if (this.controller === controller) this._cancelRequest();
      }

      /* The element may have been replaced by a cart re-render while we waited. */
      if (!this.isConnected) return;

      this._renderAnswer(zip, payload);
    }

    _renderAnswer(zip, payload) {
      this._setLoading(false);
      this._setButtonLabel(this.config.text.buttonRepeat);

      if (!payload || payload.ok === false) {
        this._renderFailure();
        return;
      }
      if (payload.enabled === false) {
        this._featureOff();
        return;
      }

      if (payload.available === false) {
        const reason = payload.reason;
        if (reason === 'disabled') {
          this._featureOff();
          return;
        }
        if (reason === 'empty_cart') {
          this._hide();
          return;
        }
        if (NON_US_REASONS.indexOf(reason) !== -1) {
          this._renderMessage(zip, this.config.text.nonUsTitle, this.config.html.nonUsBody, true);
        } else {
          this._renderMessage(zip, this.config.text.unknownTitle, this.config.html.unknownBody, true);
        }
        return;
      }

      this.lastResult = { zip: zip, payload: payload };
      this._renderResult();
    }

    /* --- states ------------------------------------------------------------ */

    _setLoading(on) {
      if (on) {
        this.panel.setAttribute('aria-busy', 'true');
        if (this.button) this.button.setAttribute('aria-busy', 'true');
      } else {
        this.panel.removeAttribute('aria-busy');
        if (this.button) this.button.removeAttribute('aria-busy');
      }
    }

    _clearInvalid() {
      this.input.removeAttribute('aria-invalid');
      this.input.removeAttribute('aria-describedby');
    }

    _renderLoading() {
      this._clearInvalid();
      this._setLoading(true);
      if (this.buttonLabel) {
        this.buttonLabel.innerHTML =
          '<span class="kbw-ship__spin" aria-hidden="true"></span>' +
          '<span>' + escapeHtml(this.config.text.loading) + '</span>';
      }
      /* The skeleton is the two shapes the answer will be, in the places it
         will be — so nothing jumps when the number replaces them. */
      this.panel.innerHTML =
        '<div><div class="kbw-ship__sk kbw-ship__sk--a"></div>' +
        '<div class="kbw-ship__sk kbw-ship__sk--b"></div>' +
        '<span class="kbw-ship__sr">' + escapeHtml(this.config.text.loadingSr) + '</span></div>';
    }

    _renderMessage(zip, title, bodyHtml, invalid) {
      const values = { zip: escapeHtml(zip) };
      this.panel.innerHTML =
        '<div class="kbw-ship__msg">' + INFO_ICON + '<div>' +
        (title ? '<strong>' + fill(escapeHtml(title), values) + '</strong>' : '') +
        fill(bodyHtml, values) +
        '</div></div>';
      this._renderProgress(null);
      if (invalid) {
        this.input.setAttribute('aria-invalid', 'true');
        this.input.setAttribute('aria-describedby', this.panel.id);
      }
    }

    /* Never red, never the word "error", and the second sentence is the one
       that matters: checkout still works. The owner can choose silence
       instead — a quiet absence beats an apology on some shops. */
    _renderFailure() {
      this._writeSummary('');
      if (this.config.onFailure === 'hide') {
        this._hide();
        return;
      }
      this._clearInvalid();
      this.panel.innerHTML =
        '<div class="kbw-ship__msg">' + INFO_ICON + '<div>' +
        '<strong>' + escapeHtml(this.config.text.errorTitle) + '</strong>' +
        appendInline(
          this.config.html.errorBody,
          '<button class="kbw-ship__link" type="button" data-kbw-ship-retry>' +
            escapeHtml(this.config.text.retry) + '</button>'
        ) +
        '</div></div>';
      this._renderProgress(null);
    }

    _renderResult() {
      if (!this.lastResult) return;
      const zip = this.lastResult.zip;
      const payload = this.lastResult.payload;
      const format = this.config.moneyFormat;
      const free = payload.freeShipping || {};

      this._clearInvalid();

      /* Free shipping is a rule, not an estimate: the field has nothing left
         to ask and the "an estimate" sentence would be hedging a certainty. */
      if (free.applied === true) {
        this.form.hidden = true;
        if (this.intro) this.intro.hidden = true;
        this._renderProgress(null);
        /* Both amounts are the service's own: it prices the cart from the
           store's prices, never from what this page sent it. */
        this.panel.innerHTML =
          '<div class="kbw-ship__free">' + TICK_ICON + '<div>' +
          '<strong>' + escapeHtml(this.config.text.freeTitle) + '</strong>' +
          fill(this.config.html.freeBody, {
            threshold: free.threshold ? money(free.threshold.cents, format) : '',
            subtotal: free.subtotal ? money(free.subtotal.cents, format) : '',
          }) +
          '</div></div>';
        this._writeSummary(this.config.text.freeTitle);
        return;
      }

      this.form.hidden = false;
      if (this.intro) this.intro.hidden = false;

      const total = payload.total ? money(payload.total.cents, format) : '';
      const label = fill(escapeHtml(this.config.text.resultLabel), {
        zip: '<span class="kbw-ship__zip">' + escapeHtml(zip) + '</span>',
      });
      const rows = this._breakdownRows(payload, format);
      const mode = this.config.breakdown;
      const showRows = mode === 'always' || (mode === 'on_request' && this.breakdownOpen);

      let html = '';
      if (showRows) html += rows;
      html +=
        '<div class="kbw-ship__row' + (showRows ? ' kbw-ship__row--sum' : '') + '">' +
        '<span class="kbw-ship__rlabel">' + label + '</span>' +
        '<span class="kbw-ship__amount">' + escapeHtml(total) + '</span></div>';

      /* No rows to show means no "what's in this estimate?" — a disclosure
         that opens on nothing is worse than none at all. */
      if (mode === 'on_request' && rows) {
        html +=
          '<button class="kbw-ship__disclose" type="button" data-kbw-ship-disclose aria-expanded="' +
          (this.breakdownOpen ? 'true' : 'false') + '">' +
          chevron(this.breakdownOpen) + ' ' +
          escapeHtml(this.breakdownOpen ? this.config.text.discloseHide : this.config.text.disclose) +
          '</button>';
      }

      this.panel.innerHTML = html;
      this._renderProgress(payload);

      /* The one line that survives a collapse. See _writeSummary. */
      this._writeSummary(
        fill(this.config.text.resultLabel, { zip: zip }) + (total ? ' — ' + total : '')
      );
    }

    /* KBW 2026-09-30: the box collapses now, and this is the half that makes
       that safe. The form folds away; the ANSWER does not.

       The previous decision was that this box is always open, and the reasoning
       was sound and is worth keeping: "the number is the whole point of the
       feature, so it is never left behind a chevron." What changed is the cost.
       On a real cart the open form pushes the second cabinet out of view
       entirely — the customer cannot see what they are buying — which was not
       visible when the always-open call was made. So the form collapses and the
       number stays, which is the only version that honours both.

       The host owns the slot; this writes into whichever one is beside it. A
       surface that offers none is simply unaffected. */
    _writeSummary(text) {
      const mount = this.closest('[data-kbw-ship-mount]');
      const slot = mount && mount.querySelector('[data-kbw-ship-summary]');
      if (!slot) return;
      slot.textContent = text || '';
      slot.hidden = !text;
    }

    /* The rows exactly as the endpoint sends them: already netted, one per
       charge, no percentages and nothing to reconstruct the rules from. The
       three labels the theme knows the shape of are the owner's words; a ZIP
       rule keeps the name the client gave that rule in the app. */
    _breakdownRows(payload, format) {
      const list = Array.isArray(payload.breakdown) ? payload.breakdown : [];
      if (this.config.breakdown === 'never') return '';

      return list
        .map((row) => {
          if (!row || !row.amount) return '';
          let label = row.label || '';
          if (row.kind === 'distance' && this.config.text.rowDistance) label = this.config.text.rowDistance;
          if (row.kind === 'size' && this.config.text.rowSize) label = this.config.text.rowSize;
          if (row.kind === 'size' && row.quantity && this.config.text.rowQty) {
            label += ' · ' + fill(this.config.text.rowQty, { count: row.quantity });
          }
          return (
            '<div class="kbw-ship__brow"><span>' + escapeHtml(label) + '</span>' +
            '<span>' + escapeHtml(money(row.amount.cents, format)) + '</span></div>'
          );
        })
        .join('');
    }

    /* "Nearly there" — cart page only; the drawer cannot carry it without
       pushing Checkout below the fold on a phone.

       Every figure is the service's: it sends the threshold whenever free
       shipping is switched on, earned or not, along with the cart's subtotal
       at the store's own prices and what is still missing. Nothing is
       subtracted here and no copy of the threshold lives in the theme — a
       second copy is a second place for it to be wrong.

       An unavailable answer carries no freeShipping at all, so the bar goes
       with it rather than standing there with the last cart's numbers. */
    _renderProgress(payload) {
      if (!this.progress) return;
      const hide = () => {
        this.progress.hidden = true;
        this.progress.innerHTML = '';
      };

      const free = (payload && payload.freeShipping) || null;
      if (this.config.context !== 'cart' || !free) return hide();
      if (free.enabled !== true || free.applied === true) return hide();
      if (!free.remaining || !free.threshold || Number(free.remaining.cents) <= 0) return hide();

      /* Only inside the owner's window: a cart nowhere near the threshold is
         never taunted with how far it has to go. */
      const window_ = Number(this.config.nearlyWindowPercent) || 25;
      const threshold = Number(free.threshold.cents);
      const remaining = Number(free.remaining.cents);
      if (!threshold || remaining > (threshold * window_) / 100) return hide();

      const reached = free.subtotal ? Number(free.subtotal.cents) : threshold - remaining;
      const percent = Math.max(0, Math.min(100, Math.round((reached / threshold) * 100)));

      this.progress.hidden = false;
      this.progress.innerHTML =
        '<div class="kbw-ship__progress"><div class="kbw-ship__ptext">' +
        fill(this.config.html.nearlyText, { amount: money(remaining, this.config.moneyFormat) }) +
        '</div><div class="kbw-ship__ptrack" role="img" aria-label="' + percent +
        ' percent of the way to free shipping"><div class="kbw-ship__pfill" style="width:' +
        percent + '%"></div></div></div>';
    }

    _onPanelClick(event) {
      const disclose = event.target.closest('[data-kbw-ship-disclose]');
      if (disclose) {
        this.breakdownOpen = !this.breakdownOpen;
        this._renderResult();
        const again = this.panel.querySelector('[data-kbw-ship-disclose]');
        if (again) again.focus();
        return;
      }
      if (event.target.closest('[data-kbw-ship-retry]')) {
        this._estimate(this.input.value);
      }
    }
  }

  customElements.define('kbw-shipping-estimate', KbwShippingEstimate);
}
